import { isSupermarketContext } from './entityContext';

/** Wildcard granted to ROOT_ADMIN (and equivalent) in session permissions. */
export const PERMISSION_WILDCARD = '*';

export const PAGE_PERMISSION = Object.freeze({
  POS: 'page.pos',
  ORDERS: 'page.orders',
  MENU: 'page.menu',
  PURCHASE: 'page.purchase',
  FINANCE: 'page.finance',
  INVENTORY: 'page.inventory',
  USER_MANAGEMENT: 'page.user_management',
  HIRING: 'page.hiring',
  BUSINESS_PROFILE: 'page.business_profile',
  ROLES: 'page.roles',
});

export const FUNCTION_PERMISSION = Object.freeze({
  POS_UNPAID_BILLS: 'fn.pos_unpaid_bills',
  ORDER_STATUS_UPDATE: 'fn.order_status_update',
  USER_MANAGE: 'fn.user_manage',
  ROLE_MANAGE: 'fn.role_manage',
  PROFILE_EDIT: 'fn.profile_edit',
});

/** Sidebar tab id → page permission code. */
export const TAB_PAGE_PERMISSION = Object.freeze({
  'dine-in': PAGE_PERMISSION.POS,
  pos: PAGE_PERMISSION.POS,
  orders: PAGE_PERMISSION.ORDERS,
  restaurants: PAGE_PERMISSION.MENU,
  purchase: PAGE_PERMISSION.PURCHASE,
  finance: PAGE_PERMISSION.FINANCE,
  inventory: PAGE_PERMISSION.INVENTORY,
  'user-management': PAGE_PERMISSION.USER_MANAGEMENT,
  hiring: PAGE_PERMISSION.HIRING,
  'business-profile': PAGE_PERMISSION.BUSINESS_PROFILE,
  roles: PAGE_PERMISSION.ROLES,
});

const DEFAULT_TAB_PRIORITY = [
  'dine-in',
  'pos',
  'orders',
  'restaurants',
  'purchase',
  'finance',
  'inventory',
  'user-management',
  'hiring',
  'business-profile',
  'roles',
];

export function getPartnerSessionUser() {
  try {
    return JSON.parse(localStorage.getItem('user') || 'null');
  } catch {
    return null;
  }
}

export function getPartnerSessionRole() {
  const user = getPartnerSessionUser();
  return String(user?.userRole || user?.role || '')
    .trim()
    .toUpperCase();
}

/**
 * Session permission codes from partner login.
 * - Explicit array (including []) comes from the API.
 * - ROOT_ADMIN without a list is treated as ["*"].
 * - Legacy sessions with no `permissions` field return null (unrestricted) until re-login.
 */
export function getSessionPermissions() {
  const user = getPartnerSessionUser();
  if (!user) return [];

  if (Object.prototype.hasOwnProperty.call(user, 'permissions')) {
    const raw = user.permissions;
    if (Array.isArray(raw)) {
      return raw.map((code) => String(code || '').trim()).filter(Boolean);
    }
  }

  if (getPartnerSessionRole() === 'ROOT_ADMIN') {
    return [PERMISSION_WILDCARD];
  }

  return null;
}

export function hasPermission(code, permissions = getSessionPermissions()) {
  if (!code) return false;
  if (permissions == null) return true;
  const list = Array.isArray(permissions) ? permissions : [];
  if (list.includes(PERMISSION_WILDCARD)) return true;
  return list.includes(String(code));
}

export function hasAnyPage(permissions = getSessionPermissions()) {
  if (permissions == null) return true;
  if (hasPermission(PERMISSION_WILDCARD, permissions)) return true;
  return (Array.isArray(permissions) ? permissions : []).some((code) =>
    String(code).startsWith('page.')
  );
}

export function canAccessUnpaidBills(permissions = getSessionPermissions()) {
  return hasPermission(FUNCTION_PERMISSION.POS_UNPAID_BILLS, permissions);
}

export function canUpdateOrderStatus(permissions = getSessionPermissions()) {
  return hasPermission(FUNCTION_PERMISSION.ORDER_STATUS_UPDATE, permissions);
}

export function canManageUsers(permissions = getSessionPermissions()) {
  return hasPermission(FUNCTION_PERMISSION.USER_MANAGE, permissions);
}

export function canManageRoles(permissions = getSessionPermissions()) {
  return hasPermission(FUNCTION_PERMISSION.ROLE_MANAGE, permissions);
}

export function canEditRestaurantStoreProfile(permissions = getSessionPermissions()) {
  return hasPermission(FUNCTION_PERMISSION.PROFILE_EDIT, permissions);
}

export function canAccessOrdersTab(permissions = getSessionPermissions()) {
  return hasPermission(PAGE_PERMISSION.ORDERS, permissions);
}

/** Staff with any admin page land on the admin shell (replaces cashier/self-checkout name checks). */
export function isPosStaff(permissions = getSessionPermissions()) {
  return hasAnyPage(permissions);
}

export function getDefaultPosTab() {
  return isSupermarketContext() ? 'pos' : 'dine-in';
}

/**
 * Allowed admin sidebar tab ids for the current permission set.
 * null = no permission restriction (full admin, subject to service filters).
 */
export function getAllowedAdminTabs(permissions = getSessionPermissions()) {
  if (permissions == null || hasPermission(PERMISSION_WILDCARD, permissions)) {
    return null;
  }

  const allowed = new Set();
  const list = Array.isArray(permissions) ? permissions : [];

  for (const code of list) {
    if (code === PAGE_PERMISSION.POS) {
      allowed.add('dine-in');
      allowed.add('pos');
      continue;
    }
    for (const [tabId, pageCode] of Object.entries(TAB_PAGE_PERMISSION)) {
      if (pageCode === code) allowed.add(tabId);
    }
  }

  return allowed;
}

export function filterSidebarItemsByPermission(
  sidebarItems,
  permissions = getSessionPermissions()
) {
  const allowed = getAllowedAdminTabs(permissions);
  if (!allowed) return sidebarItems;
  return sidebarItems.filter(([id]) => allowed.has(id));
}

/** @deprecated Prefer filterSidebarItemsByPermission */
export const filterSidebarItemsByRole = filterSidebarItemsByPermission;

export function resolveAllowedAdminTab(requestedTab, permissions = getSessionPermissions()) {
  const allowed = getAllowedAdminTabs(permissions);
  const fallback = getDefaultPosTab();
  if (!allowed) return requestedTab || fallback;
  if (requestedTab && allowed.has(requestedTab)) return requestedTab;

  for (const tabId of DEFAULT_TAB_PRIORITY) {
    if (allowed.has(tabId)) {
      if (tabId === 'dine-in' || tabId === 'pos') return getDefaultPosTab();
      return tabId;
    }
  }
  return fallback;
}
